(() => {
  const DEVICE_STORAGE_KEY = 'dyefor:selected-device';
  const DEVICE_TYPE_STORAGE_KEY = 'dyefor:selected-device-type';

  const clean = (value) => String(value || '').trim();

  class DyeforHomeDevice extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      this.summary = this.querySelector('[data-home-device-open]');
      this.drawer = this.querySelector('[data-home-device-drawer]');
      this.panel = this.querySelector('.dyefor-home-device__drawer-panel');
      this.label = this.querySelector('[data-home-device-label]');
      this.value = this.querySelector('[data-home-device-value]');
      this.shopLink = this.querySelector('[data-home-device-shop]');
      this.search = this.querySelector('[data-home-device-search]');
      this.empty = this.querySelector('[data-home-device-empty]');
      this.tabs = Array.from(this.querySelectorAll('[data-home-device-tab]'));
      this.typePanels = Array.from(this.querySelectorAll('[data-home-device-type-panel]'));
      this.options = Array.from(this.querySelectorAll('[data-home-device-option]'));
      this.lastTrigger = null;

      this.bindEvents();
      this.restoreSelection();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeyDown);
      window.removeEventListener('pageshow', this.onPageShow);
      window.removeEventListener('storage', this.onStorage);
    }

    bindEvents() {
      this.summary?.addEventListener('click', () => this.openDrawer());
      this.querySelectorAll('[data-home-device-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeDrawer());
      });

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.setActiveType(tab.dataset.homeDeviceTab));
      });

      this.options.forEach((option) => {
        option.addEventListener('click', () => this.selectDevice(option));
      });

      this.search?.addEventListener('input', () => this.filterOptions());

      this.onKeyDown = (event) => {
        if (!this.classList.contains('is-open')) return;
        if (event.key === 'Escape') {
          this.closeDrawer();
          return;
        }
        if (event.key !== 'Tab' || !this.panel) return;

        const focusable = Array.from(this.panel.querySelectorAll(
          'button:not([hidden]):not(:disabled), input:not([hidden]):not(:disabled), a[href]:not([hidden]), [tabindex]:not([tabindex="-1"]):not([hidden])'
        )).filter((element) => element.offsetParent !== null);
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      };
      document.addEventListener('keydown', this.onKeyDown);

      this.onPageShow = () => this.restoreSelection();
      window.addEventListener('pageshow', this.onPageShow);

      this.onStorage = (event) => {
        if (event.key === DEVICE_STORAGE_KEY || event.key === DEVICE_TYPE_STORAGE_KEY) {
          this.restoreSelection();
        }
      };
      window.addEventListener('storage', this.onStorage);
    }

    readStoredValue(key) {
      try {
        return clean(window.localStorage.getItem(key));
      } catch (error) {
        return '';
      }
    }

    storeSelection(value, type) {
      try {
        window.localStorage.setItem(DEVICE_STORAGE_KEY, value);
        window.localStorage.setItem(DEVICE_TYPE_STORAGE_KEY, type);
      } catch (error) {
        // The current-page selection still works when storage is unavailable.
      }
    }

    inferType(value) {
      const key = clean(value).toLowerCase();
      if (/airpod|ipad|macbook|mac book/.test(key)) return 'tech';
      return 'phone';
    }

    inferFamily(value) {
      const key = clean(value).toLowerCase();
      if (key.includes('airpod')) return 'airpods';
      if (key.includes('ipad')) return 'ipad';
      if (/macbook|mac book/.test(key)) return 'macbook';
      if (/samsung|galaxy|^s\d|^a\d|^note\s|^m\d|^j\d/.test(key)) return 'samsung';
      return 'iphone';
    }

    familyDetails(family) {
      const details = {
        iphone: { label: 'Selected phone', link: this.dataset.phoneUrl, action: 'Shop phone cases' },
        samsung: { label: 'Selected phone', link: this.dataset.samsungUrl, action: 'Shop phone cases' },
        airpods: { label: 'Selected tech', link: this.dataset.airpodsUrl, action: 'Shop AirPods cases' },
        ipad: { label: 'Selected tech', link: this.dataset.ipadUrl, action: 'Shop iPad cases' },
        macbook: { label: 'Selected tech', link: this.dataset.macbookUrl, action: 'Shop MacBook cases' },
      };
      return details[family] || details.iphone;
    }

    restoreSelection() {
      const storedValue = this.readStoredValue(DEVICE_STORAGE_KEY);
      const storedType = this.readStoredValue(DEVICE_TYPE_STORAGE_KEY) || this.inferType(storedValue);
      const matchingOption = this.options.find((option) => (
        clean(option.dataset.deviceValue).toLowerCase() === storedValue.toLowerCase()
      ));
      const family = matchingOption?.dataset.deviceFamily || this.inferFamily(storedValue);
      const type = matchingOption?.dataset.deviceType || storedType;
      this.updateSelection(storedValue, type, family);
      this.setActiveType(type || 'phone');
    }

    updateSelection(value, type, family) {
      const selectedValue = clean(value);
      const selectedType = type === 'tech' ? 'tech' : 'phone';
      const selectedFamily = family || this.inferFamily(selectedValue);
      const details = this.familyDetails(selectedFamily);

      if (this.label) this.label.textContent = selectedValue ? details.label : 'Your device';
      if (this.value) this.value.textContent = selectedValue || 'Choose your phone or tech';
      if (this.summary) {
        this.summary.setAttribute(
          'aria-label',
          selectedValue ? `Change device. Current selection: ${selectedValue}` : 'Choose your phone or tech device'
        );
      }

      this.options.forEach((option) => {
        const isSelected = clean(option.dataset.deviceValue) === selectedValue;
        option.classList.toggle('is-selected', isSelected);
        option.setAttribute('aria-selected', String(isSelected));
      });

      if (this.shopLink) {
        this.shopLink.textContent = details.action;
        this.shopLink.href = this.withDevice(details.link, selectedValue);
        this.shopLink.hidden = !selectedValue;
      }

      (this.closest('.dyefor-personal-home') || document).querySelectorAll('[data-home-device-link]').forEach((link) => {
        const accepts = clean(link.dataset.homeDeviceLink).split(' ');
        const baseUrl = link.dataset.deviceBaseUrl || link.getAttribute('href');
        link.href = selectedValue && accepts.includes(selectedFamily)
          ? this.withDevice(baseUrl, selectedValue)
          : baseUrl;
      });

      this.selectedValue = selectedValue;
      this.selectedType = selectedType;
      this.selectedFamily = selectedFamily;
    }

    withDevice(target, value) {
      if (!target) return '#';
      try {
        const url = new URL(target, window.location.origin);
        if (value) url.searchParams.set('device', value);
        return `${url.pathname}${url.search}${url.hash}`;
      } catch (error) {
        return target;
      }
    }

    selectDevice(option) {
      const value = clean(option.dataset.deviceValue);
      const type = clean(option.dataset.deviceType) || this.inferType(value);
      const family = clean(option.dataset.deviceFamily) || this.inferFamily(value);
      if (!value) return;

      this.storeSelection(value, type);
      this.updateSelection(value, type, family);
      this.closeDrawer();
      window.dispatchEvent(new CustomEvent('dyefor:device-change', {
        detail: { value, type, family },
      }));
    }

    setActiveType(type) {
      const activeType = type === 'tech' ? 'tech' : 'phone';
      this.activeType = activeType;
      this.tabs.forEach((tab) => {
        const isActive = tab.dataset.homeDeviceTab === activeType;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', String(isActive));
        tab.tabIndex = isActive ? 0 : -1;
      });
      this.typePanels.forEach((panel) => {
        panel.hidden = panel.dataset.homeDeviceTypePanel !== activeType;
      });
      if (this.search) this.search.value = '';
      this.filterOptions();
    }

    filterOptions() {
      const query = clean(this.search?.value).toLowerCase();
      let visibleCount = 0;

      this.typePanels.forEach((typePanel) => {
        if (typePanel.hidden) return;
        typePanel.querySelectorAll('[data-home-device-family]').forEach((family) => {
          const familyOptions = Array.from(family.querySelectorAll('[data-home-device-option]'));
          let familyVisible = 0;
          familyOptions.forEach((option) => {
            const isVisible = !query || clean(option.textContent).toLowerCase().includes(query);
            option.hidden = !isVisible;
            if (isVisible) familyVisible += 1;
          });
          family.hidden = familyVisible === 0;
          visibleCount += familyVisible;
        });
      });

      if (this.empty) this.empty.hidden = visibleCount > 0;
    }

    openDrawer() {
      if (!this.drawer) return;
      this.lastTrigger = document.activeElement;
      this.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.summary?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('dyefor-home-device-drawer-open');
      this.setActiveType(this.selectedType || 'phone');
      window.requestAnimationFrame(() => this.search?.focus());
    }

    closeDrawer() {
      if (!this.drawer) return;
      this.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.summary?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('dyefor-home-device-drawer-open');
      this.lastTrigger?.focus();
    }
  }

  if (!customElements.get('dyefor-home-device')) {
    customElements.define('dyefor-home-device', DyeforHomeDevice);
  }
})();
